"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import CardCarousel from "./CardCarousel";
import { getLocalizedPath } from "@/lib/slugs";

export interface BlogPostItem {
    id: string | number;
    link: string;
    image: string;
    title: string;
    description: string;
    category: string;
    readTime: string;
}

export interface BlogSectionDict {
    common?: { general?: string };
    home?: {
        blog?: {
            title?: string;
            description?: string;
            see_all?: string;
            read_time?: string;
            no_posts?: string;
        };
    };
}

export default function BlogSectionCarousel({
    posts,
    dict,
    lang = "tr",
}: {
    posts: BlogPostItem[];
    dict?: BlogSectionDict;
    lang?: string;
}) {
    const seeAllLabel = dict?.home?.blog?.see_all || "See All Posts";

    const card = (post: BlogPostItem) => (
        <Link href={post.link} className="group block h-full">
            {/* Image */}
            <div className="rounded-xl overflow-hidden mb-4 h-[220px] relative shadow-sm">
                <Image
                    src={post.image}
                    alt={post.title}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                    sizes="(max-width: 640px) 82vw, (max-width: 1024px) 58vw, 33vw"
                />
            </div>

            {/* Meta */}
            <div className="flex items-center space-x-3 mb-2">
                <span className="bg-[#E0F4FB] text-[#0f4c81] text-[11px] font-semibold px-3 py-1 rounded-full">
                    {post.category || dict?.common?.general}
                </span>
                <span className="text-gray-600 text-[12px] font-medium">
                    {post.readTime} {dict?.home?.blog?.read_time}
                </span>
            </div>

            {/* Content */}
            <h3 className="text-[#0f4c81] font-bold text-lg mb-2 leading-tight group-hover:text-[#00AEEF] transition-colors">
                {post.title}
            </h3>
            <p className="text-gray-500 text-[13px] leading-relaxed line-clamp-3">
                {post.description}
            </p>
        </Link>
    );

    return (
        <section className="py-14 bg-gray-50">
            <div className="container-custom">
                <div className="mb-8 md:mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="text-left">
                        <h2 className="text-2xl font-bold text-[#0f4c81] mb-2">{dict?.home?.blog?.title}</h2>
                        <p className="text-gray-500 text-[14px]">
                            {dict?.home?.blog?.description}
                        </p>
                    </div>
                    <Link
                        href={getLocalizedPath("blog", lang as "tr" | "en" | "ar")}
                        className="group inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-full bg-[#0f4c81] px-5 py-2.5 text-[14px] font-semibold text-white shadow-sm transition-all hover:bg-[#00AEEF] hover:-translate-y-0.5 sm:self-auto"
                    >
                        {seeAllLabel}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                </div>

                {posts.length === 0 ? (
                    <div className="py-10 text-center text-gray-600">
                        {dict?.home?.blog?.no_posts}
                    </div>
                ) : (
                    <>
                        {/* Desktop: grid */}
                        <div className="hidden lg:grid grid-cols-3 gap-6">
                            {posts.map((post) => (
                                <div key={post.id}>{card(post)}</div>
                            ))}
                        </div>

                        {/* Mobile / tablet: carousel */}
                        <div className="lg:hidden">
                            <CardCarousel
                                ariaLabel="post"
                                itemClassName="w-[82%] sm:w-[58%] md:w-[44%]"
                                items={posts.map((post) => card(post))}
                            />
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
